import type { Node as RFNode, Edge as RFEdge } from "@xyflow/react";
import type { Workflow, WorkflowNode, WorkflowEdge, WorkflowNodeType } from "@/types/aiToolkit";

/**
 * Pure converters between the engine's Workflow graph (ai_workflows JSON) and
 * React Flow's Node/Edge arrays. The engine JSON is the source of truth — the
 * canvas never invents a schema. Kept pure + unit-tested (graphConvert.test.ts).
 */

function subtitleFor(n: WorkflowNode): string | undefined {
  const d = n.data;
  if (d.agentId) return `agent: ${d.agentId.slice(0, 12)}`;
  if (d.toolId) return d.toolOperation ? `${d.toolId} · ${d.toolOperation}` : d.toolId;
  if (d.knowledgeSource) return `src: ${d.knowledgeSource}`;
  if (d.condition) return d.condition.slice(0, 24);
  return undefined;
}

export function toReactFlow(wf: Pick<Workflow, "nodes" | "edges">): { nodes: RFNode[]; edges: RFEdge[] } {
  const nodes: RFNode[] = (wf.nodes ?? []).map((n) => ({
    id: n.id,
    type: "canvas",
    position: { x: n.position?.x ?? 0, y: n.position?.y ?? 0 },
    data: { nodeType: n.type, label: n.data?.label ?? n.type, subtitle: subtitleFor(n) },
  }));
  const edges: RFEdge[] = (wf.edges ?? []).map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle,
    targetHandle: e.targetHandle,
    label: e.label ?? e.condition ?? (e.dataMapping ? `⤷ ${Object.keys(e.dataMapping).join(", ")}` : undefined),
    // carry dataMapping through the round-trip (4A.5)
    data: e.dataMapping ? { dataMapping: e.dataMapping } : undefined,
  }));
  return { nodes, edges };
}

/**
 * Merge React Flow positions/edges back into the authoritative engine nodes,
 * preserving every `data.*` field the canvas doesn't edit. `prevNodes` carries
 * the full node data; RF nodes only carry position + a display label.
 */
export function toWorkflowGraph(
  rfNodes: RFNode[],
  rfEdges: RFEdge[],
  prevNodes: WorkflowNode[],
): { nodes: WorkflowNode[]; edges: WorkflowEdge[] } {
  const prevById = new Map(prevNodes.map((n) => [n.id, n]));
  const nodes: WorkflowNode[] = rfNodes.map((rf) => {
    const prev = prevById.get(rf.id);
    const nodeType = ((rf.data as { nodeType?: WorkflowNodeType })?.nodeType ?? prev?.type ?? "prompt") as WorkflowNodeType;
    return {
      id: rf.id,
      type: nodeType,
      position: { x: Math.round(rf.position.x), y: Math.round(rf.position.y) },
      data: prev?.data ?? { label: (rf.data as { label?: string })?.label ?? nodeType },
    };
  });
  const edges: WorkflowEdge[] = rfEdges.map((e) => {
    const dm = (e.data as { dataMapping?: Record<string, string> } | undefined)?.dataMapping;
    return {
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle ?? undefined,
      targetHandle: e.targetHandle ?? undefined,
      label: typeof e.label === "string" && !e.label.startsWith("⤷") ? e.label : undefined,
      dataMapping: dm && Object.keys(dm).length ? dm : undefined,
    };
  });
  return { nodes, edges };
}
